<script setup lang="ts">
/**
 * The call's light: a canvas beacon that is the call's face. A soft orange sphere whose edge ripples with
 * the voice it hears (mic level while you talk, output level while Lighthouse talks), breathing while it
 * listens, with the lighthouse beam sweeping while it thinks, rings on loud peaks, amber pulses when it
 * needs an OK, grey when muted, dimmed on hold, red on an error.
 *
 * Levels come from a plain object read every frame (not reactive), so the Vue tree never re-renders at
 * 60 fps. Reduced motion: no rotation, rings or ripples; level drives brightness only.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { CallPhase } from '@/voice/types'

const props = withDefaults(defineProps<{
    phase: CallPhase
    muted?: boolean
    /** { mic, out } 0..1, read every frame. */
    levels: { mic: number; out: number }
    /** Rendered size in CSS px (the canvas follows it). */
    size?: number
}>(), { muted: false, size: 240 })

const canvas = ref<HTMLCanvasElement | null>(null)
const reduced = ref(false)
let mq: MediaQueryList | null = null
let raf = 0
let ctx: CanvasRenderingContext2D | null = null
let dpr = 1
let energy = 0
let beam = 0
let last = 0
let lastPeak = 0
const rings: { born: number; strength: number }[] = []
/** Colors ease between states instead of snapping. */
const tint = { r: 251, g: 76, b: 31, gray: 0, dim: 1, amber: 0, red: 0 }

const mode = computed(() => {
    if (props.phase === 'error') return 'error'
    if (props.phase === 'onHold') return 'hold'
    if (props.phase === 'ended') return 'ended'
    if (props.muted && props.phase !== 'speaking') return 'muted'
    if (props.phase === 'needsApproval') return 'approval'
    if (props.phase === 'thinking' || props.phase === 'sending') return 'thinking'
    if (props.phase === 'speaking') return 'speaking'
    if (props.phase === 'userSpeaking') return 'hearing'
    if (props.phase === 'connecting') return 'connecting'
    return 'listening'
})

function resize() {
    const el = canvas.value
    if (!el) return
    dpr = Math.min(3, window.devicePixelRatio || 1)
    const px = Math.round(props.size * dpr)
    if (el.width !== px) {
        el.width = px
        el.height = px
    }
    ctx = el.getContext('2d')
}

watch(() => props.size, resize)

function approach(cur: number, target: number, dt: number, ms: number) {
    return cur + (target - cur) * (1 - Math.exp(-dt / ms))
}

function draw(now: number) {
    raf = requestAnimationFrame(draw)
    const c = ctx
    if (!c) return
    const dt = last ? Math.min(64, now - last) : 16
    last = now
    const t = now / 1000
    const m = mode.value
    const still = reduced.value

    // ── how much the light is moving ──
    const heard = m === 'hearing' || m === 'listening' ? props.levels.mic : 0
    const said = m === 'speaking' ? props.levels.out : 0
    let target = Math.max(heard, said)
    if (m === 'listening') target = Math.max(target, 0.06 + 0.05 * Math.sin(t * (2 * Math.PI / 2.4)))
    if (m === 'thinking') target = 0.14 + 0.05 * Math.sin(t * 3.1)
    if (m === 'connecting') target = 0.1 + 0.1 * (0.5 + 0.5 * Math.sin(t * 3.6))
    if (m === 'approval') target = 0.12 + 0.06 * Math.sin(t * 4.2)
    if (m === 'muted' || m === 'hold' || m === 'ended') target = 0.05
    if (m === 'error') target = 0.08
    energy = approach(energy, target, dt, target > energy ? 50 : 170)

    tint.gray = approach(tint.gray, m === 'muted' || m === 'ended' ? 1 : 0, dt, 220)
    tint.dim = approach(tint.dim, m === 'hold' ? 0.4 : m === 'ended' ? 0.55 : 1, dt, 260)
    tint.amber = approach(tint.amber, m === 'approval' ? 1 : 0, dt, 260)
    tint.red = approach(tint.red, m === 'error' ? 1 : 0, dt, 260)

    const W = c.canvas.width
    const cx = W / 2
    const R = W / 2
    c.clearRect(0, 0, W, W)
    c.save()
    c.globalAlpha = tint.dim

    // Base hue: orange, mixed toward amber / red / grey by state.
    const mix = (a: number[], b: number[], k: number) => a.map((v, i) => v + (b[i] - v) * k)
    let rgb = [251, 76, 31]
    rgb = mix(rgb, [255, 176, 32], tint.amber)
    rgb = mix(rgb, [255, 69, 58], tint.red)
    rgb = mix(rgb, [142, 142, 147], tint.gray)
    const [r, g, b] = rgb.map(Math.round)
    const deep = rgb.map((v) => Math.round(v * 0.62))
    const e = still ? 0.25 + energy * 0.5 : energy

    // ── bloom: a wide soft light that swells with the voice ──
    const bloom = c.createRadialGradient(cx, cx, R * 0.1, cx, cx, R)
    bloom.addColorStop(0, `rgba(${r},${g},${b},${0.42 + 0.35 * e})`)
    bloom.addColorStop(0.38, `rgba(${r},${g},${b},${0.16 + 0.2 * e})`)
    bloom.addColorStop(0.7, `rgba(${r},${g},${b},${0.04 + 0.06 * e})`)
    bloom.addColorStop(1, `rgba(${r},${g},${b},0)`)
    c.fillStyle = bloom
    c.fillRect(0, 0, W, W)

    // ── the beam (thinking): a lighthouse sweep ──
    if (m === 'thinking' && !still) {
        beam += (dt / 1000) * Math.PI * 2 * 0.6
        c.save()
        c.globalCompositeOperation = 'lighter'
        c.translate(cx, cx)
        // Two opposed beams, each feathered from a few stacked wedges so the edges stay soft.
        for (const [off, alpha] of [[0, 1], [Math.PI, 0.4]] as const) {
            for (const [deg, a] of [[56, 0.32], [36, 0.42], [18, 0.5]] as const) {
                c.save()
                c.rotate(beam + off)
                const wedge = (Math.PI / 180) * deg
                const bg = c.createRadialGradient(0, 0, R * 0.2, 0, 0, R * 0.99)
                bg.addColorStop(0, `rgba(255,226,206,${0.22 * alpha * a})`)
                bg.addColorStop(0.5, `rgba(${r},${g},${b},${0.12 * alpha * a})`)
                bg.addColorStop(1, `rgba(${r},${g},${b},0)`)
                c.fillStyle = bg
                c.beginPath()
                c.moveTo(0, 0)
                c.arc(0, 0, R * 0.99, -wedge / 2, wedge / 2)
                c.closePath()
                c.fill()
                c.restore()
            }
        }
        c.restore()
    }

    // ── rings on loud peaks (and an amber pulse while it waits for an OK) ──
    if (!still && (m === 'hearing' || m === 'speaking') && energy > 0.45 && now - lastPeak > 560) {
        lastPeak = now
        rings.push({ born: now, strength: Math.min(1, energy) })
    }
    if (m === 'approval' && !still && now - lastPeak > 1300) {
        lastPeak = now
        rings.push({ born: now, strength: 1 })
    }
    for (let i = rings.length - 1; i >= 0; i--) {
        const k = (now - rings[i].born) / 1000
        if (k >= 1) {
            rings.splice(i, 1)
            continue
        }
        const ease = 1 - Math.pow(1 - k, 3)
        c.strokeStyle = `rgba(${Math.min(255, r + 40)},${Math.min(255, g + 90)},${Math.min(255, b + 80)},${(1 - k) * 0.42 * rings[i].strength})`
        c.lineWidth = (2.4 - 1.6 * k) * dpr
        c.beginPath()
        c.arc(cx, cx, R * (0.5 + 0.46 * ease), 0, Math.PI * 2)
        c.stroke()
    }

    const base = R * (0.42 + 0.11 * e)

    // ── aura: translucent fluid layers around the core, rippling with the voice ──
    const blob = (radius: number, amp: number, phase: number, speed: number) => {
        const pts = 96
        c.beginPath()
        for (let i = 0; i <= pts; i++) {
            const a = (i / pts) * Math.PI * 2
            const wob = Math.sin(a * 3 + t * speed + phase) * 0.55 + Math.sin(a * 2 - t * speed * 0.7 + phase * 2) * 0.45
                + Math.sin(a * 5 + t * speed * 1.3 - phase) * 0.25
            const rad = radius * (1 + amp * wob)
            const x = cx + Math.cos(a) * rad
            const y = cx + Math.sin(a) * rad
            if (i === 0) c.moveTo(x, y)
            else c.lineTo(x, y)
        }
        c.closePath()
    }
    c.save()
    c.globalCompositeOperation = 'lighter'
    const amp = still ? 0 : 0.035 + 0.085 * e
    const layers: [number, number, number, number[], number][] = [
        [1.24 + 0.22 * e, 1.15, 0.0, [r, g, b], 0.22 + 0.2 * e],
        [1.14 + 0.16 * e, 1.6, 2.1, mix(rgb, [255, 170, 90], 0.6 * (1 - tint.gray)).map(Math.round), 0.18 + 0.18 * e],
        [1.08 + 0.1 * e, 2.2, 4.2, mix(rgb, [255, 120, 160], 0.25 * (1 - tint.gray) * (1 - tint.amber)).map(Math.round), 0.14 + 0.14 * e],
    ]
    for (const [scale, speed, phase, col, alpha] of layers) {
        const rad = base * scale
        blob(rad, amp, phase, still ? 0 : speed)
        // Fades out before the rippling edge, so the layers read as moving light, not bands.
        const lg = c.createRadialGradient(cx, cx, base * 0.7, cx, cx, rad * (1 + amp))
        const [cr, cg, cb] = col
        lg.addColorStop(0, `rgba(${cr},${cg},${cb},${alpha})`)
        lg.addColorStop(0.45, `rgba(${cr},${cg},${cb},${alpha * 0.5})`)
        lg.addColorStop(0.78, `rgba(${cr},${cg},${cb},${alpha * 0.12})`)
        lg.addColorStop(1, `rgba(${cr},${cg},${cb},0)`)
        c.fillStyle = lg
        c.fill()
    }
    c.restore()

    // ── the core: a warm glass sphere ──
    const lx = cx - base * 0.3
    const ly = cx - base * 0.38
    c.save()
    c.beginPath()
    c.arc(cx, cx, base, 0, Math.PI * 2)
    c.shadowColor = `rgba(${r},${g},${b},${0.55 + 0.3 * e})`
    c.shadowBlur = R * 0.28
    const core = c.createRadialGradient(lx, ly, base * 0.02, cx, cx + base * 0.1, base * 1.05)
    const light = mix([255, 252, 248], [236, 236, 240], tint.gray).map(Math.round)
    const peach = mix([255, 205, 182], [206, 206, 212], tint.gray).map(Math.round)
    core.addColorStop(0, `rgb(${light.join(',')})`)
    core.addColorStop(0.22, `rgb(${peach.join(',')})`)
    core.addColorStop(0.62, `rgb(${r},${g},${b})`)
    core.addColorStop(1, `rgb(${deep.join(',')})`)
    c.fillStyle = core
    c.fill()
    c.shadowBlur = 0
    c.clip()

    // Light moving inside the glass (faster while it thinks).
    if (!still) {
        const swirl = t * (m === 'thinking' ? 1.6 : 0.45)
        const sx = cx + Math.cos(swirl) * base * 0.45
        const sy = cx + Math.sin(swirl) * base * 0.45
        const inner = c.createRadialGradient(sx, sy, 0, sx, sy, base * 0.9)
        inner.addColorStop(0, `rgba(255,236,214,${0.22 + 0.25 * e})`)
        inner.addColorStop(1, 'rgba(255,236,214,0)')
        c.fillStyle = inner
        c.fillRect(cx - base, cx - base, base * 2, base * 2)
    }
    // Depth: a darker crescent at the bottom edge.
    const rim = c.createRadialGradient(cx, cx - base * 0.25, base * 0.7, cx, cx, base * 1.02)
    rim.addColorStop(0, 'rgba(0,0,0,0)')
    rim.addColorStop(1, `rgba(${deep[0] * 0.5},${deep[1] * 0.5},${deep[2] * 0.5},0.35)`)
    c.fillStyle = rim
    c.fillRect(cx - base, cx - base, base * 2, base * 2)
    // Specular highlight.
    const spec = c.createRadialGradient(lx, ly, 0, lx, ly, base * 0.5)
    spec.addColorStop(0, 'rgba(255,255,255,0.75)')
    spec.addColorStop(0.5, 'rgba(255,255,255,0.18)')
    spec.addColorStop(1, 'rgba(255,255,255,0)')
    c.fillStyle = spec
    c.fillRect(cx - base, cx - base, base * 2, base * 2)
    c.restore()

    c.restore()
}

onMounted(() => {
    mq = window.matchMedia?.('(prefers-reduced-motion: reduce)') ?? null
    reduced.value = Boolean(mq?.matches)
    mq?.addEventListener?.('change', onMotion)
    resize()
    raf = requestAnimationFrame(draw)
})

function onMotion(e: MediaQueryListEvent) {
    reduced.value = e.matches
}

onBeforeUnmount(() => {
    cancelAnimationFrame(raf)
    mq?.removeEventListener?.('change', onMotion)
})
</script>

<template>
    <canvas
        ref="canvas"
        class="vc-orb"
        :style="{ width: `${size}px`, height: `${size}px` }"
        aria-hidden="true"
    />
</template>

<style scoped>
.vc-orb {
    display: block;
}
</style>
